/* eslint-disable @typescript-eslint/dot-notation */
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    OnDestroy,
    signal,
    viewChildren,
} from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { DateFormatEnum } from '../../models/date-format.enum';
import { DateInputEnum } from '../../models/date-input.enum';

import { CommonModule, DatePipe } from '@angular/common';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipModule } from '@angular/material/tooltip';
import { ConfigData } from '../../models/configData';
import { valueToMoment } from '../../utils/dates-utils';

export interface IDateCellRendererParams {
    required?: boolean;
    inputType?: DateInputEnum;
    inputFormat?: DateFormatEnum;
    dateSeparator?: string;
    timeSeparator?: string;
}

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'se-date-cell-renderer',
    templateUrl: './date-cell-renderer.component.html',
    styleUrls: ['./date-cell-renderer.component.scss'],
    providers: [{ provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: ConfigData.tooltipConfiguration }, DatePipe],
    imports: [MatTooltipModule, CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateCellRendererComponent implements ICellRendererAngularComp, OnDestroy {
    readonly params = signal<(ICellRendererParams & IDateCellRendererParams) | null>(null);
    readonly datePipe = inject(DatePipe);
    readonly inputType = signal<DateInputEnum | null>(null);
    readonly inputFormat = signal<DateFormatEnum | null>(null);
    readonly value = signal<moment.Moment | null>(null);
    readonly dateSeparator = signal<string>('');
    readonly timeSeparator = signal<string>('');

    readonly disabled = signal<boolean>(false);

    readonly viewFormat = computed<string>(() => {
        const inputType = this.inputType();
        const inputFormat = this.inputFormat();
        const dateSeparator = this.dateSeparator();
        const timeSeparator = this.timeSeparator();
        switch (inputType) {
            case DateInputEnum.DATE_TIME:
                if (inputFormat === DateFormatEnum.SHORT) {
                    return `dd${dateSeparator}MM${dateSeparator}yyyy, HH${timeSeparator}mm`;
                } else {
                    return 'dd MMMM, y HH:mm:ss';
                }

            case DateInputEnum.DATE:
                if (inputFormat === DateFormatEnum.SHORT) {
                    return `dd${dateSeparator}MM${dateSeparator}yyyy`;
                } else {
                    return 'dd MMMM, y';
                }

            case DateInputEnum.TIME:
                if (inputFormat === DateFormatEnum.SHORT) {
                    return `HH${timeSeparator}mm`;
                } else {
                    return 'HH:mm:ss';
                }
        }
        return '';
    });

    readonly tooltipValue = computed<string>(() => {
        const viewFormat = this.viewFormat();
        const date = this.value()?.toDate();
        if (!date) return '';
        return this.datePipe.transform(date, viewFormat) ?? '';
    });

    readonly cellContents = viewChildren<ElementRef<HTMLElement>>('cellContent');

    private ro?: ResizeObserver;

    constructor() {
        effect(() => {
            const cellContents = this.cellContents();
            if (cellContents) {
                requestAnimationFrame(() => this.enableOrDisableTooltip());
            }
        });
    }

    agInit(params: ICellRendererParams & IDateCellRendererParams): void {
        this.params.set(params);
        const value = params.value;
        this.value.set(valueToMoment(value));
        this.inputType.set(params.inputType ?? DateInputEnum.DATE);
        this.inputFormat.set(params.inputFormat ?? DateFormatEnum.SHORT);
        this.dateSeparator.set(params.dateSeparator ?? '/');
        this.timeSeparator.set(params.timeSeparator ?? ':');
        this.ro?.disconnect();
        this.ro = new ResizeObserver((_) => {
            // Logica da eseguire quando la cella cambia dimensione
            requestAnimationFrame(() => this.enableOrDisableTooltip());
        });
        this.ro.observe(params.eGridCell); // Ascolta la cella stessa
    }

    refresh(params: ICellRendererParams & IDateCellRendererParams): boolean {
        const value = params.value;
        this.value.set(valueToMoment(value));
        requestAnimationFrame(() => this.enableOrDisableTooltip());
        return true;
    }

    private enableOrDisableTooltip() {
        const params = this.params();
        const cellContents = this.cellContents();
        if (!params) return;
        if (!cellContents) return;
        if (!cellContents || cellContents.length === 0) {
            this.disabled.set(true);
        } else {
            const elementRefsArraySorted = [...cellContents].sort((a, b) => {
                let aOffsetWidth = a.nativeElement.offsetWidth;
                let bOffsetWidth = b.nativeElement.offsetWidth;
                return aOffsetWidth - bOffsetWidth;
            });
            const element = elementRefsArraySorted.pop()?.nativeElement;
            if (!element) return;
            const isTruncated = element.scrollWidth > element.clientWidth;
            this.disabled.set(!isTruncated);
        }
    }

    ngOnDestroy(): void {
        this.ro?.disconnect();
        this.ro = undefined;
    }
}
